import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useId, type ReactNode } from "react";
import { Button } from "react-aria-components";
import { useNavigate } from "react-router";
import { Symbol } from "../design/Symbol";
import type { Zustandsanzeige } from "./jagdzustand";
import { useWechselmarke } from "./wechselmarke";

/**
 * Zustandsband (oberflaeche.md §4.1, design/README §9.2): `region` „Jagdzustand“ ganz oben, außerhalb von `main`.
 * Statusleiste als `button` mit dem Zustandsbegriff (Symbol, Begriff, zweite Zeile per `aria-describedby`, Pfeil), dann
 * genau eine Zeile in fester Höhe: Kontaktband ohne Netz, sonst Wechselband nach einem Wechsel (Wechselmarke, §7), sonst
 * Notrufband bzw. Kontextzeile. Live-Region für den Wechsel. Der Gegenbegriff erscheint nirgends. Solange der
 * Jagdzustand noch nicht bekannt ist (erstes Lesen nach dem Öffnen), zeigt das Band keinen Zustandsbegriff.
 *
 * Arten (Referenz `design/werkzeuge/vorschau/ansichten.py` `band`):
 * - `start`: Startansicht mit Kontextzeile;
 * - `liste`: Ansicht mit Kopf und Liste (kompaktes Band: „Zurück“, Statusleiste, „mehr“ in einer Zeile, darunter der Titel);
 * - `ablauf`: Ablauf mit Titel und Schrittanzeige (§5); `notruf`, `anruf`: Notrufblatt und Anruf (Titel auf gelber Platte);
 * - `dialog`: Bestätigungsdialog (Titel ohne „Zurück“).
 */
export type Bandart = "start" | "liste" | "ablauf" | "notruf" | "anruf" | "dialog";

export interface Titelzeile {
  titel: string;
  unter?: ReactNode;
  schritte?: string;
  /** Titel auf der Notruf-Titelplatte mit diesem Symbol (Notrufblatt, Anruf) */
  symbol?: string;
}

const KLASSE: Record<Bandart, string> = {
  start: "band",
  liste: "band band--liste",
  ablauf: "band band--kopf band--ablauf",
  notruf: "band band--kopf band--notruf band--ohne-zurueck",
  anruf: "band band--kopf band--notruf band--anruf",
  dialog: "band band--kopf band--ohne-zurueck",
};

function Titel({ titel, unter, schritte, symbol }: Titelzeile) {
  return (
    <div className="leiste titel">
      {symbol ? <b className="titel__t notruf-titel"><Symbol name={symbol} /><span>{titel}</span></b> : <b className="titel__t">{titel}</b>}
      {unter ? <span className="titel__unter">{unter}</span> : null}
      {schritte ? <span className="titel__schritte">{schritte}</span> : null}
    </div>
  );
}

export function Zustandsband({ drueckjagdId, anzeige, art = "start", kontext, titel, status, eigeneFlaeche }: {
  drueckjagdId: string; anzeige: Zustandsanzeige; art?: Bandart;
  /** Kontextzeile der Startansicht (Rolle, Stand, Warteschlange, „Offline bereit“) */
  kontext?: ReactNode;
  titel?: Titelzeile;
  /** Statuszeile mit eigener Aussage (z. B. Notrufband eines eingehenden Notrufs) */
  status?: ReactNode;
  /** in einer neutralen Ansicht: das Band trägt die Zustandsfarbe selbst */
  eigeneFlaeche?: boolean;
}) {
  const navigate = useNavigate();
  const id = useId();
  const weg = anzeige.kontakt.keinKontaktSeit;
  const wechsel = useWechselmarke(drueckjagdId, anzeige);
  // Ansage des Wechsels (Live-Region, §4.1): solange die Wechselmarke steht; ein Abgleich der Uhr beim Öffnen ist kein Wechsel
  const ansage = wechsel ? `Neu: ${anzeige.darstellung.text}` : "";

  const kontaktband = weg ? <div className="leiste kontaktband" role="status"><Symbol name="offline" /><span>kein Kontakt seit {berlinUhrzeit(weg)}</span></div> : null;
  const wechselband = wechsel ? (
    <div className="leiste wechselband" role="status"><b>Neu: {anzeige.darstellung.text}</b><span>{wechsel.text}</span></div>
  ) : null;
  // genau eine Statuszeile: kein Kontakt vor dem Wechsel vor eigener Aussage (Sicherheit zuerst, design/README §9.2)
  const statuszeile = kontaktband ?? wechselband ?? status ?? null;
  const mitTitel = art !== "start";
  const klasse = `${KLASSE[art]}${mitTitel && statuszeile ? " band--status" : ""}`;
  return (
    <header className={klasse} role="region" aria-label="Jagdzustand" aria-busy={anzeige.bekannt ? undefined : true}
      data-zustand={eigeneFlaeche ? anzeige.flaeche : undefined}>
      {anzeige.bekannt ? (
        <Button className={`zs${wechsel ? " zs--wechsel" : ""}`} aria-labelledby={`${id}-b`} aria-describedby={`${id}-z`}
          onPress={() => navigate(`/drueckjagd/${drueckjagdId}/jagdzustand`)}>
          <Symbol name={anzeige.darstellung.symbol} className="zs__sym" />
          <span className="zs__text">
            <span className="zs__begriff" id={`${id}-b`}>{anzeige.darstellung.text}</span>
            <span className="zs__zeit" id={`${id}-z`}>{anzeige.zeile}</span>
          </span>
          <Symbol name="weiter" className="zs__pfeil" />
        </Button>
      ) : (
        <div className="zs" aria-hidden="true"><Symbol name="uhr" className="zs__sym" /></div>
      )}
      {mitTitel ? (titel ? <Titel {...titel} /> : null) : (statuszeile ?? <div className="leiste ctx">{kontext}</div>)}
      {mitTitel ? statuszeile : null}
      <div className="visuell-verborgen" role="status" aria-live="assertive">{ansage}</div>
    </header>
  );
}

/**
 * Kontextzeile der Startansicht (design/README §9.2, Vertrag §4.1): Rollensymbol und `Stand <Nr> · <Name>` (bricht nur
 * zwischen Wertgruppen; bei niedriger Ansicht oder großer Schrift nur die erste), dann die Hinweise als `status`.
 */
export function Kontextzeile({ symbol, teile, children }: { symbol: string; teile: string[]; children?: ReactNode }) {
  const [erst, ...rest] = teile;
  return (
    <>
      <span className="ctx__wer">
        <Symbol name={symbol} />
        <span className="ctx__text">
          {erst ? <span className="ctx__teil">{erst}</span> : null}
          {rest.length ? <span className="ctx__neben"> · <span className="ctx__teil">{rest.join(" · ")}</span></span> : null}
        </span>
      </span>
      {children}
    </>
  );
}
